/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useStoreDispatch } from "../../../store/hooks";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { deviceTemplateSelector, selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { basicDialogSelector, isLoadingStatusSelector } from "../../../store/helpers/helperSelectors";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { setSelectedType } from "../../../store/api/devices/reducers";
import ApplyTemplates from "./ApplyTemplates";
import FileImport from "../../Shared/FileImport/FileImport";
import { IFullDeviceDetails } from "../../../store/api/deviceConfig/types";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { ISelectedTemplate } from "../../../store/api/devices/types";
import { Button } from "../../ui/button";
import { Loader2 } from "lucide-react";

const LocalBulkTemplate: React.FC = () => {
    const dispatch = useStoreDispatch();
    const location = useLocation();
    const devicePathKey = location?.pathname?.split("/")[2];
    const basicDialogOpenFor = useSelector(basicDialogSelector);
    const selectedTemplate: ISelectedTemplate = useSelector(deviceTemplateSelector);
    const isLoadingStatus = useSelector(isLoadingStatusSelector);
    const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
    const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

    const localTemplateClickHandler = () => {
        dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.LOCALTEMPLATE));
        dispatch(setSelectedType("local"));
    }
    const localImportTemplateClickHandler = () => {
        dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.IMPORTMODAL));
        dispatch(setSelectedType("local"));
    }
    return (
        <>
            {basicDialogOpenFor === DIALOGOPENFOR.LOCALTEMPLATE && <ApplyTemplates modelOpenMode={"Add"} />}
            {basicDialogOpenFor === DIALOGOPENFOR.IMPORTMODAL && <FileImport modelOpenMode={"Add"} />}
            <div className={`grid grid-cols-[auto_1fr_auto] items-center w-full px-4 py-3 rounded-md transition-colors duration-200 hover:bg-muted ${selectedDevice?.nodeType === "SN" ? "border-b border-border" : ""}`}>

                {/* Left */}
                <div>
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#dbeafe] dark:bg-[#1d2e54]">
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-[#c5d2e4] dark:bg-[#344365] text-primary dark:text-[#38BDF8] text-sm font-medium">
                            2
                        </span>
                        <span className="text-sm font-medium text-primary dark:text-[#38BDF8]">
                            Local/bulk template
                        </span>
                    </div>
                </div>

                {/* Center */}
                <div className="flex justify-center items-center text-sm">
                    {selectedTemplate?.deviceKey === devicePathKey && (
                        <>
                            <span className="text-muted-foreground">Template Name:&nbsp;</span>
                            <span className="text-foreground">{selectedTemplate?.templateName || ""}</span>
                        </>
                    )}
                </div>

                {/* Right */}
                <div className="flex justify-end gap-2">
                    {permissions?.hasOpCodeGroup("manage_sn") && (
                        <>
                            <Button
                                onClick={localImportTemplateClickHandler}
                                variant="outline"
                                size="sm"
                                disabled={isLoadingStatus["importFile"]}
                            >
                                {isLoadingStatus["importFile"] && (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                )}
                                Import
                            </Button>
                            <Button
                                onClick={localTemplateClickHandler}
                                variant="outline"
                                size="sm"
                                disabled={isLoadingStatus["apply"]}
                            >
                                {isLoadingStatus["apply"] && (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                )}
                                Apply
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    )

}
export default LocalBulkTemplate;